import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Tabs,
  Card,
  Form,
  Input,
  Button,
  Upload,
  Switch,
  message,
  Table,
  Modal,
  Select,
  Space,
  Divider,
  Row,
  Col,
  Typography,
  Tag,
  Popconfirm,
} from "antd";
import {
  UploadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BankOutlined,
  GlobalOutlined,
  SettingOutlined,
  FileTextOutlined,
  LogoutOutlined,
} from "@ant-design/icons";

import "./CompanyManagement.css";

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const API_BASE = import.meta.env.VITE_API_BASE_URL;

const getImageUrl = (url) => {
  if (!url) return "";

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  const baseUrl = API_BASE.replace(/\/api\/?$/, "");

  if (url.startsWith("/uploads/")) {
    return `${baseUrl}${url}`;
  }

  if (url.startsWith("uploads/")) {
    return `${baseUrl}/${url}`;
  }

  return url;
};

const CompanyManagement = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");

  // Modal states
  const [bankAccountModalVisible, setBankAccountModalVisible] = useState(false);
  const [editingBankAccount, setEditingBankAccount] = useState(null);

  // Forms
  const [profileForm] = Form.useForm();
  const [bankAccountForm] = Form.useForm();
  const [termsForm] = Form.useForm();

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

const fetchCompanyProfile = async () => {
  setLoading(true);

  try {
    const token = localStorage.getItem("token");

    const response = await fetch(
      `${API_BASE}/company-profile`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (response.ok) {
      const result = await response.json();

const data = result.data || {};

setCompanyProfile(data);

profileForm.setFieldsValue(data);

termsForm.setFieldsValue({
  termsContent:
    data?.termsAndConditions?.content || "",

  paymentPolicy:
    data?.paymentPolicy || "",

  refundPolicy:
    data?.refundPolicy || "",

  defaultInclusions:
    data?.defaultInclusions || [],

  defaultExclusions:
    data?.defaultExclusions || [],
});

      } else {
        message.error("Failed to fetch company profile");
      }
    } catch (error) {
      console.error("Error fetching company profile:", error);
      message.error("Error fetching company profile");
    } finally {
      setLoading(false);
    }
  };

const handleProfileUpdate = async (values) => {
  setLoading(true);

  try {

    const token =
      localStorage.getItem("token");

    const response = await fetch(
     `${API_BASE}/company-profile/update`,
      {
        method: "PUT",

        headers: {
          Authorization:
            `Bearer ${token}`,

          "Content-Type":
            "application/json",
        },

        body: JSON.stringify({

          officialName:
            values.officialName,

          displayName:
            values.displayName,

          businessId:
            values.businessId,

          email:
            values.email,

          website:
            values.website,

          address:
            values.address || {},

          documentBranding:
            values.documentBranding || {}

        }),
      }
    );

    const data =
      await response.json();

    if (response.ok) {

      message.success(
        "Company profile updated successfully"
      );

      fetchCompanyProfile();

    } else {

      message.error(
        data.message ||
        "Failed to update profile"
      );
    }

  } catch (error) {

    console.error(
      "Error updating profile:",
      error
    );

    message.error(
      "Error updating profile"
    );

  } finally {

    setLoading(false);
  }
};


const handleFileUpload = async (file, type) => {
  const formData = new FormData();
  formData.append(type, file);
  formData.append("alt", `Company ${type}`);

  try {
    const token = localStorage.getItem("token");

    const response = await fetch(
      `${API_BASE}/company-profile/upload-${type}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      }
    );

      if (response.ok) {
        message.success(
          `${type.charAt(0).toUpperCase() + type.slice(1)} uploaded successfully`,
        );
        fetchCompanyProfile();
        return false; // Prevent default upload behavior
      } else {
        message.error(`Failed to upload ${type}`);
      }
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      message.error(`Error uploading ${type}`);
    }
    return false;
  };


  const handleAddBankAccount = async (values) => {
    try {
      const token = localStorage.getItem("token");
      const url = editingBankAccount
        ? `/api/company-profile/bank-accounts/${editingBankAccount._id}`
        : "/company-profile/bank-accounts";

      const method = editingBankAccount ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      if (response.ok) {
        message.success(
          `Bank account ${editingBankAccount ? "updated" : "added"} successfully`,
        );
        setBankAccountModalVisible(false);
        bankAccountForm.resetFields();
        setEditingBankAccount(null);
        fetchCompanyProfile();
      } else {
        const errorData = await response.json();
        message.error(
          errorData.message ||
            `Failed to ${editingBankAccount ? "update" : "add"} bank account`,
        );
      }
    } catch (error) {
      console.error("Error with bank account:", error);
      message.error("Error with bank account");
    }
  };

  const handleDeleteBankAccount = async (accountId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `/api/company-profile/bank-accounts/${accountId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        message.success("Bank account deleted successfully");
        fetchCompanyProfile();
      } else {
        const errorData = await response.json();
        message.error(errorData.message || "Failed to delete bank account");
      }
    } catch (error) {
      console.error("Error deleting bank account:", error);
      message.error("Error deleting bank account");
    }
  };

const handleUpdateTerms = async (values) => {
  try {

    const updateData = {
      settings: {

        termsAndConditions: {
          content:
            values.termsContent || "",

          lastUpdated:
            new Date(),
        },

        paymentPolicy:
          values.paymentPolicy || "",

        refundPolicy:
          values.refundPolicy || "",

        defaultInclusions:
          values.defaultInclusions || [],

        defaultExclusions:
          values.defaultExclusions || [],
      },
    };

    const token =
      localStorage.getItem("token");

    const response = await fetch(
      "/settings",
      {
        method: "PUT",

        headers: {
          Authorization:
            `Bearer ${token}`,

          "Content-Type":
            "application/json",
        },

        body: JSON.stringify(
          updateData
        ),
      }
    );

    if (response.ok) {

      message.success(
        "Terms & Policies updated successfully"
      );

      fetchCompanyProfile();

    } else {

      const errorData =
        await response.json();

      message.error(
        errorData.message ||
        "Failed to update settings"
      );
    }

  } catch (error) {

    console.error(
      "Error updating settings:",
      error
    );

    message.error(
      "Error updating settings"
    );
  }
};



  const bankAccountColumns = [
    {
      title: "Bank Name",
      dataIndex: "bankName",
      key: "bankName",
    },
    {
      title: "Account Holder",
      dataIndex: "accountHolderName",
      key: "accountHolderName",
    },
    {
      title: "Account Number",
      dataIndex: "accountNumber",
      key: "accountNumber",
      render: (text) => `****${text.slice(-4)}`,
    },
    {
      title: "IFSC Code",
      dataIndex: "ifscCode",
      key: "ifscCode",
    },
    {
      title: "Type",
      dataIndex: "accountType",
      key: "accountType",
      render: (text) => <Tag color="blue">{text.toUpperCase()}</Tag>,
    },
    {
      title: "Default",
      dataIndex: "isDefault",
      key: "isDefault",
      render: (isDefault) =>
        isDefault ? <Tag color="green">Default</Tag> : null,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingBankAccount(record);
              bankAccountForm.setFieldsValue(record);
              setBankAccountModalVisible(true);
            }}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete bank account"
            description="Are you sure you want to delete this bank account?"
            onConfirm={() => handleDeleteBankAccount(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="dashboard-layout">
      <div className="main-content">
        <div className="company-management">
          <div className="page-header">
            <div className="header-content">
              <Title level={2}>🏢 Company Management</Title>
              <Text type="secondary">
                Manage your company profile, branding, and
                settings
              </Text>
            </div>
            <Button
              type="primary"
              danger
              icon={<LogoutOutlined />}
              onClick={logout}
              size="large"
            >
              Logout
            </Button>
          </div>

          <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
            <TabPane
              tab={
                <span>
                  <SettingOutlined />
                  Organization Profile
                </span>
              }
              key="profile"
            >
              <Card title="Company Information" loading={loading}>
                <Form
                  form={profileForm}
                  layout="vertical"
                  onFinish={handleProfileUpdate}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="officialName"
                        label="Official/Registered Name"
                        rules={[
                          {
                            required: true,
                            message: "Please enter official name",
                          },
                        ]}
                      >
                        <Input placeholder="Enter official company name" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="displayName"
                        label="Display/Brand Name"
                        rules={[
                          {
                            required: true,
                            message: "Please enter display name",
                          },
                        ]}
                      >
                        <Input placeholder="Enter display name" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item name="businessId" label="Business ID">
                        <Input placeholder="Enter business registration ID" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                          { required: true, message: "Please enter email" },
                          {
                            type: "email",
                            message: "Please enter valid email",
                          },
                        ]}
                      >
                        <Input placeholder="Enter company email" />
                      </Form.Item>
                    </Col>
                  </Row>

<Form.Item name="website" label="Website">
  <Input placeholder="Enter company website" />
</Form.Item>

<Divider>Quotation Branding</Divider>

<Row gutter={16}>
  <Col span={12}>

<Form.Item
  name={["documentBranding", "showHeader"]}
  label="Show Header Banner"
  valuePropName="checked"
>
  <Switch />
</Form.Item>

<Form.Item
  name={["documentBranding", "showFooter"]}
  label="Show Footer Banner"
  valuePropName="checked"
>
  <Switch />
</Form.Item>

  </Col>
</Row>

<Divider>Address Information</Divider>

                  <Form.Item
                    name={["address", "street"]}
                    label="Street Address"
                  >
                    <Input placeholder="Enter street address" />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item name={["address", "city"]} label="City">
                        <Input placeholder="Enter city" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name={["address", "state"]} label="State">
                        <Input placeholder="Enter state" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name={["address", "zipCode"]} label="ZIP Code">
                        <Input placeholder="Enter ZIP code" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item name={["address", "country"]} label="Country">
                    <Input placeholder="Enter country" />
                  </Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Update Profile
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </TabPane>

            <TabPane
              tab={
                <span>
                  <UploadOutlined />
                  Branding & Logo
                </span>
              }
              key="branding"
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Card title="Company Logo" size="small">
                    {companyProfile?.brandLogo?.url && (
                      <div className="logo-preview">
                        <img
                          src={getImageUrl(companyProfile.brandLogo.url)}
                          alt="Company Logo"
                          style={{ maxWidth: "100%", maxHeight: "200px" }}
                        />
                      </div>
                    )}
                    <Upload
                      beforeUpload={(file) => handleFileUpload(file, "logo")}
                      showUploadList={false}
                      accept="image/*"
                    >
                      <Button icon={<UploadOutlined />} block>
                        Upload Logo
                      </Button>
                    </Upload>
                  </Card>
                </Col>
                <Col span={8}>
                  <Card title="Header Banner" size="small">
                    {companyProfile?.headerBanner?.url && (
                      <div className="banner-preview">
                        <img
                          src={getImageUrl(companyProfile.headerBanner.url)}
                          alt="Header Banner"
                          style={{ maxWidth: "100%", maxHeight: "200px" }}
                        />
                      </div>
                    )}
                    <Upload
                      beforeUpload={(file) => handleFileUpload(file, "header")}
                      showUploadList={false}
                      accept="image/*"
                    >
                      <Button icon={<UploadOutlined />} block>
                        Upload Header
                      </Button>
                    </Upload>
                  </Card>
                </Col>
                <Col span={8}>
                  <Card title="Footer Banner" size="small">
                    {companyProfile?.footerBanner?.url && (
                      <div className="banner-preview">
                        <img
                          src={getImageUrl(companyProfile.footerBanner.url)}
                          alt="Footer Banner"
                          style={{ maxWidth: "100%", maxHeight: "200px" }}
                        />
                      </div>
                    )}
                    <Upload
                      beforeUpload={(file) => handleFileUpload(file, "footer")}
                      showUploadList={false}
                      accept="image/*"
                    >
                      <Button icon={<UploadOutlined />} block>
                        Upload Footer
                      </Button>
                    </Upload>
                  </Card>
                </Col>
              </Row>

            </TabPane>


            <TabPane
              tab={
                <span>
                  <BankOutlined />
                  Bank Accounts
                </span>
              }
              key="banking"
            >
              <Card
                title="Bank Account Details"
                extra={
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingBankAccount(null);
                      bankAccountForm.resetFields();
                      setBankAccountModalVisible(true);
                    }}
                  >
                    Add Bank Account
                  </Button>
                }
              >
                <Table
                  columns={bankAccountColumns}
                  dataSource={companyProfile?.bankAccounts || []}
                  rowKey="_id"
                  pagination={{ pageSize: 10 }}
                />
              </Card>
            </TabPane>

            <TabPane
              tab={
                <span>
                  <FileTextOutlined />
                  Terms & Conditions
                </span>
              }
              key="terms"
            >
              <Card title="Terms & Conditions">
                <Form
                  form={termsForm}
                  layout="vertical"
                  onFinish={handleUpdateTerms}
                >
                  <Form.Item name="termsContent" label="Terms & Conditions">
                    <TextArea
                      rows={10}
                      placeholder="Enter your terms and conditions..."
                    />
                  </Form.Item>

<Form.Item
  name="paymentPolicy"
  label="Payment Policy"
>
  <Input.TextArea
    rows={5}
    placeholder="Enter payment policy"
  />
</Form.Item>

<Form.Item
  name="refundPolicy"
  label="Cancellation / Refund Policy"
>
  <Input.TextArea
    rows={5}
    placeholder="Enter refund policy"
  />
</Form.Item>


<Divider>
  Default Inclusions
</Divider>

<Form.Item
  name="defaultInclusions"
  label="Default Inclusions"
  extra="
Press Enter after each inclusion
  "
>

  <Select
    mode="tags"

    style={{
      width: "100%",
    }}

    placeholder="
Add default inclusions
    "

    tokenSeparators={[","]}
  />

</Form.Item>

<Divider>
  Default Exclusions
</Divider>

<Form.Item
  name="defaultExclusions"
  label="Default Exclusions"
  extra="
Press Enter after each exclusion
  "
>

  <Select
    mode="tags"

    style={{
      width: "100%",
    }}

    placeholder="
Add default exclusions
    "

    tokenSeparators={[","]}
  />

</Form.Item>

                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading}>
                      Update Terms & Conditions
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </TabPane>
          </Tabs>


          {/* Bank Account Modal */}
          <Modal
            title={
              editingBankAccount ? "Edit Bank Account" : "Add New Bank Account"
            }
            open={bankAccountModalVisible}
            onCancel={() => {
              setBankAccountModalVisible(false);
              setEditingBankAccount(null);
              bankAccountForm.resetFields();
            }}
            footer={null}
          >
            <Form
              form={bankAccountForm}
              layout="vertical"
              onFinish={handleAddBankAccount}
            >
              <Form.Item
                name="bankName"
                label="Bank Name"
                rules={[{ required: true, message: "Please enter bank name" }]}
              >
                <Input placeholder="Enter bank name" />
              </Form.Item>

              <Form.Item
                name="accountHolderName"
                label="Account Holder Name"
                rules={[
                  {
                    required: true,
                    message: "Please enter account holder name",
                  },
                ]}
              >
                <Input placeholder="Enter account holder name" />
              </Form.Item>

              <Form.Item
                name="accountNumber"
                label="Account Number"
                rules={[
                  { required: true, message: "Please enter account number" },
                ]}
              >
                <Input placeholder="Enter account number" />
              </Form.Item>

              <Form.Item
                name="ifscCode"
                label="IFSC Code"
                rules={[{ required: true, message: "Please enter IFSC code" }]}
              >
                <Input placeholder="Enter IFSC code" />
              </Form.Item>

              <Form.Item
                name="accountType"
                label="Account Type"
                rules={[
                  { required: true, message: "Please select account type" },
                ]}
              >
                <Select placeholder="Select account type">
                  <Option value="savings">Savings</Option>
                  <Option value="current">Current</Option>
                  <Option value="business">Business</Option>
                </Select>
              </Form.Item>

              <Form.Item name="isDefault" valuePropName="checked">
                <input type="checkbox" /> Set as default account
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    {editingBankAccount ? "Update" : "Add"} Bank Account
                  </Button>
                  <Button
                    onClick={() => {
                      setBankAccountModalVisible(false);
                      setEditingBankAccount(null);
                      bankAccountForm.resetFields();
                    }}
                  >
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default CompanyManagement;
